import { useEffect, useState, useContext } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import {
  useDeleteCalendarEvent,
  useGetCalendarEvent,
  useGetMemberCalendarEvent,
  useGetExternalCalendarEvent,
  useGetMemberExternalCalendarEvent,
  useDeleteMemberCalendarEvent,
  useDeleteExternalCalendarEvent,
} from "services/calendar";
import {
  CalendarEvent,
  EventNotice,
  EventNotice_NoticeType,
} from "protogen/calendar_pb";
import {
  subtitle,
  getAttendeeStatusLabel,
  getAttendeeStatusLabelColor,
  getAttendeeStatusLabelBackgroundColor,
  formatRecurringDescription,
  formatReminders,
} from "./utils";
import DocumentViewer from "../editor/DocumentViewer";
import useIsMobile from "../hooks/useIsMobile";
import LinkRouter from "components/navigation/LinkRouter";
import { MapPin, Info, Pencil, Trash2, X } from "lucide-react";
import { ReactComponent as TodoListIcon } from "../../icons/Menu/TodoList.svg";
import {
  DeleteCalendarEventRequest,
  DeleteExternalCalendarEventRequest,
  GetExternalCalendarEventRequest,
} from "protogen/calendar_service_pb";
import { Family } from "protogen/advisors_service_pb";
import VideoCallInfo from "./VideoCallInfo";
import { CurrentUserContext } from "../context/RequireAuth";

interface Props {
  eventRef: string;
  onClose: (refresh?: boolean) => void;
  primaryAction: (eventRef: string) => void;
  onDelete?: (eventRef?: string) => void;
  accountType?: "advisor" | "member";
  eventNotice?: EventNotice;
  onShowDuplicateEvents: () => void;
  family?: Family;
}

const EventLocation = ({ location }: { location: string }) => {
  let formattedAddress = "";
  let mapsAddress = "";
  let mapUrl: string | null = null;
  try {
    const locationData = JSON.parse(location);

    mapsAddress = locationData.formattedAddress;
    mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      mapsAddress,
    )}`;
    if (locationData.name && locationData.formattedAddress) {
      formattedAddress = `${locationData.name} - ${locationData.formattedAddress}`;
    } else {
      formattedAddress = locationData.name || locationData.formattedAddress;
    }
  } catch (e) {
    formattedAddress = location;
  }
  if (!mapUrl && formattedAddress.startsWith("http")) {
    mapUrl = formattedAddress;
  }
  const innerContent = (
    <>
      <MapPin color="#198282" size={16} />
      <Typography variant="body" color="#198282" sx={{ fontWeight: 600 }}>
        {formattedAddress}
      </Typography>
    </>
  );
  return mapUrl ? (
    <LinkRouter
      to={mapUrl}
      targetNew={true}
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: "4px",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      {innerContent}
    </LinkRouter>
  ) : (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: "4px",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      {innerContent}
    </Box>
  );
};

const Header = (
  notice: EventNotice,
  event: CalendarEvent,
  onShowDuplicateEvents: () => void,
  isTwoWayEditable: boolean,
  family?: Family,
  userTimezone?: string,
) => {
  const location = event?.location || notice.locationString;
  const recurringDescription = event ? formatRecurringDescription(event) : "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <Box>
        {family && (
          <LinkRouter
            to={`/families/${encodeURIComponent(family.ref)}`}
            sx={{ padding: 0 }}
          >
            <Typography variant="bodyHeavy" color="primary" sx={{ padding: 0 }}>
              {family.name}
            </Typography>
          </LinkRouter>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "8px",
            alignItems: "end",
          }}
        >
          <Typography variant="h3" sx={{ wordBreak: "break-word" }}>
            {notice.title}
          </Typography>
          {notice.topics?.length > 0 && (
            <Typography variant="subtitle2">
              {notice.topics?.join(", ")}
            </Typography>
          )}
        </Box>
      </Box>
      <Typography variant="bodyHeavy" sx={{ color: "#6B6E7B" }}>
        {subtitle(notice, true, userTimezone)}
      </Typography>
      {recurringDescription && (
        <Typography variant="body" sx={{ color: "#6B6E7B" }}>
          {recurringDescription}
        </Typography>
      )}
      {location && <EventLocation location={location} />}
      {(event?.taskSource ||
        event?.sourceEntityType === "task_reminder" ||
        event?.sourceEntityType === "task_event" ||
        notice?.taskRef) && (
        <LinkRouter
          to={`/tasks/${encodeURIComponent(
            event?.taskSource?.ref ||
              event?.sourceEntityRef ||
              notice?.taskRef ||
              "",
          )}`}
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "4px",
            alignItems: "center",
          }}
        >
          <TodoListIcon height="16px" width="16px" stroke="#198282" />
          <Typography variant="body" color="#198282" sx={{ fontWeight: 600 }}>
            {event?.taskSource?.title || notice?.taskTitle || "View task"}
          </Typography>
        </LinkRouter>
      )}
    </Box>
  );
};

const Details = (event: CalendarEvent) => {
  const parsePayload = (payload: string) => {
    try {
      return JSON.parse(payload);
    } catch (e: unknown) {
      if (e instanceof SyntaxError) return payload;
      throw e;
    }
  };
  const content = event?.details?.payload
    ? event?.details?.contentType == "json"
      ? parsePayload(event?.details?.payload)
      : event?.details?.payload
    : event?.details?.textContent;
  const emptyContent = !content && !event?.details?.attachments?.length;
  if (emptyContent) return <Typography variant="body">No details</Typography>;
  return (
    <DocumentViewer
      content={content}
      attachments={event?.details?.attachments!}
      minHeight="20px"
      maxHeight="288px"
    />
  );
};

const TextDetails = (notice: EventNotice) => {
  if (!notice.textOnlyDetails.trim()) {
    return <Typography variant="body">No details</Typography>;
  }
  return (
    <Typography
      variant="body"
      sx={{
        a: {
          textDecoration: "none",
          color: "#198282",
        },
      }}
    >
      <DocumentViewer
        content={notice.textOnlyDetails}
        minHeight="20px"
        maxHeight="288px"
      />
    </Typography>
  );
};

const CalendarInfo = (
  notice: EventNotice,
  isTwoWayEditable: boolean,
  onShowDuplicateEvents: () => void,
) => {
  const isExternalEvent =
    notice.noticeType === EventNotice_NoticeType.NoticeType_EXTERNAL_EVENT;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <Typography variant="bodyHeavy">Calendar:</Typography>
        {isExternalEvent ? (
          <>
            <Typography variant="body">
              {notice.externalCalendarName}
            </Typography>
          </>
        ) : (
          <Typography variant="body">Faye calendar</Typography>
        )}
        {notice.hasDuplicate && (
          <Tooltip
            placement="right"
            title="We are only showing the Faye event. Click here to see the event across all calendars."
          >
            <Info
              style={{ cursor: "pointer" }}
              onClick={onShowDuplicateEvents}
              size={16}
              color="#198282"
            />
          </Tooltip>
        )}
      </Box>
      {notice.hasDuplicate && (
        <Typography color="text.secondary" variant="body">
          This event exists on multiple calendars.
        </Typography>
      )}
    </Box>
  );
};

const Attendees = (event: CalendarEvent | EventNotice) => {
  if (event.attendees.length === 0) return <></>;
  return (
    <Box>
      <Typography sx={{ marginBottom: "12px" }} variant="h4">
        Guests
      </Typography>
      {event.attendees.map((a) => {
        return (
          <Box
            key={a.email}
            sx={{
              display: "flex",
              flexDirection: "column",
              marginBottom: "7px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: "12px",
                maxWidth: "340px",
                alignItems: "center",
              }}
            >
              {a.firstName && a.lastName ? (
                <Tooltip title={a.email}>
                  <Typography
                    variant="body"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {a.firstName} {a.lastName}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography
                  variant="body"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {a.email}
                </Typography>
              )}
              {a.rsvpStatus === 1 || a.rsvpStatus === 2 ? (
                <Chip
                  label={getAttendeeStatusLabel(a.rsvpStatus)}
                  sx={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: getAttendeeStatusLabelColor(a.rsvpStatus),
                    backgroundColor: getAttendeeStatusLabelBackgroundColor(
                      a.rsvpStatus,
                    ),
                  }}
                />
              ) : (
                <Typography color="text.secondary">
                  {getAttendeeStatusLabel(a.rsvpStatus)}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const Reminders = (event: CalendarEvent | EventNotice) => {
  if (!event.reminders || event.reminders.length === 0) return <></>;
  const formattedReminders = formatReminders(event.reminders);
  return (
    <Box>
      <Typography sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <Typography component="span" variant="bodyHeavy">
          Reminder:
        </Typography>
        <Typography component="span" variant="body">
          {formattedReminders.join(", ")}
        </Typography>
      </Typography>
    </Box>
  );
};

export default ({
  eventRef,
  onClose,
  primaryAction,
  onDelete,
  accountType = "advisor",
  eventNotice,
  onShowDuplicateEvents,
  family,
}: Props) => {
  const location = useLocation();
  const currentUser = useContext(CurrentUserContext);
  const [event, setEvent] = useState<CalendarEvent | undefined>(undefined);
  const [notice, setNotice] = useState<EventNotice | undefined>(eventNotice);
  const [deleteMenuAnchor, setDeleteMenuAnchor] = useState<null | HTMLElement>(
    null,
  );

  const cleanEventRef = eventRef.split("?")[0];
  const queryParams = new URLSearchParams(location.search);
  const eventRefHasExternal = eventRef.includes("?external=true");
  const isExternalFromQuery =
    queryParams.get("external") === "true" || eventRefHasExternal;
  const isExternalEvent =
    eventNotice?.noticeType ===
      EventNotice_NoticeType.NoticeType_EXTERNAL_EVENT || isExternalFromQuery;
  const isRecurringEvent =
    (event?.recurringUnit !== 0 && event?.recurringInterval) ||
    (notice?.recurringUnit !== 0 && notice?.recurringInterval) ||
    !!notice?.recurringReferenceStartSec;
  const isMobile = useIsMobile();

  const isTwoWayEditable: boolean = Boolean(
    notice?.externalCalendarAccessScope === "read_write" &&
      isExternalEvent &&
      notice?.externalCalendarRef,
  );

  // Internal event hooks
  const getCalendarEventRequestFn =
    accountType === "advisor" ? useGetCalendarEvent : useGetMemberCalendarEvent;
  const {
    request,
    loading: eventLoading,
    errorCode: eventErrorCode,
  } = getCalendarEventRequestFn((r) => {
    setEvent(r?.event);
    if (r?.notice) {
      // The original event notice has the duplicate flag because it was created along side
      // all other events.
      setNotice(
        new EventNotice({
          ...r.notice,
          hasDuplicate: eventNotice?.hasDuplicate,
        }),
      );
    }
  });

  const getExternalCalendarEventRequestFn =
    accountType === "advisor"
      ? useGetExternalCalendarEvent
      : useGetMemberExternalCalendarEvent;
  const {
    request: externalRequest,
    loading: externalEventLoading,
    errorCode: externalEventErrorCode,
  } = getExternalCalendarEventRequestFn((r) => {
    if (r?.notice) {
      setNotice(
        new EventNotice({
          ...r.notice,
          hasDuplicate: eventNotice?.hasDuplicate,
        }),
      );
    }
  });
  const deleteCalendarEventRequestFn =
    accountType === "advisor"
      ? useDeleteCalendarEvent
      : useDeleteMemberCalendarEvent;
  const { request: deleteRequest, loading: deleteLoading } =
    deleteCalendarEventRequestFn();
  const { request: deleteExternalRequest, loading: deleteExternalLoading } =
    useDeleteExternalCalendarEvent();
  const loading =
    eventLoading ||
    externalEventLoading ||
    deleteLoading ||
    deleteExternalLoading;

  const deleteEvent = async (deleteSingleInstance: boolean) => {
    const cleanRef = eventRef.split("?")[0];
    if (isExternalEvent) {
      await deleteExternalRequest(
        new DeleteExternalCalendarEventRequest({
          eventRef: cleanRef,
          deleteAllInstances: !deleteSingleInstance,
        }),
      );
    } else {
      await deleteRequest(
        new DeleteCalendarEventRequest({
          eventRef: cleanRef,
          originalEventStartSec:
            isRecurringEvent && deleteSingleInstance
              ? eventNotice?.startSec!
              : undefined,
        }),
      );
    }
    onDelete && onDelete(cleanRef);
    onClose(true);
  };

  const handleDeleteClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isRecurringEvent) {
      setDeleteMenuAnchor(e.currentTarget);
    } else {
      deleteEvent(true);
    }
  };

  const handleDeleteMenuClose = () => {
    setDeleteMenuAnchor(null);
  };

  const handleDeleteThisEvent = () => {
    deleteEvent(true);
    handleDeleteMenuClose();
  };

  const handleDeleteAllEvents = () => {
    deleteEvent(false);
    handleDeleteMenuClose();
  };

  useEffect(() => {
    if (isExternalEvent) {
      // Fetch external event details
      externalRequest(
        new GetExternalCalendarEventRequest({ eventRef: cleanEventRef }),
      );
    } else {
      // Fetch internal event details
      request({ eventRef: cleanEventRef });
    }
  }, [open]);

  const errorCode = isExternalEvent ? externalEventErrorCode : eventErrorCode;
  const isDeleted = errorCode === 410;

  if (loading || (!notice && !eventNotice) || (!event && !isExternalEvent))
    return <></>;

  if (isDeleted) {
    return (
      <Box sx={{ width: "100%", textAlign: "center", padding: "48px 24px" }}>
        <Typography variant="h4" sx={{ marginBottom: "12px" }}>
          Event has been deleted
        </Typography>
        <Typography variant="body" color="text.secondary">
          This event is no longer available
        </Typography>
        <Button
          sx={{ marginTop: "24px" }}
          variant="outlined"
          onClick={() => onClose()}
        >
          Close
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header with action buttons */}
      <Box sx={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {Header(
            notice!,
            event!,
            onShowDuplicateEvents,
            isTwoWayEditable,
            family,
            currentUser?.timezone,
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexShrink: 0,
          }}
        >
          {(!isExternalEvent || isTwoWayEditable) &&
            event?.sourceEntityType !== "task_reminder" && (
              <Box sx={{ display: "flex", gap: "12px" }}>
                <IconButton
                  onClick={() => primaryAction(eventRef)}
                  sx={(theme) => ({
                    color: theme.palette.text.tertiary,
                  })}
                >
                  <Pencil size={20} />
                </IconButton>
                <IconButton
                  onClick={handleDeleteClick}
                  disabled={deleteLoading || deleteExternalLoading}
                  sx={(theme) => ({
                    color: theme.palette.text.tertiary,
                  })}
                >
                  <Trash2 size={20} />
                </IconButton>
                <Menu
                  anchorEl={deleteMenuAnchor}
                  open={Boolean(deleteMenuAnchor)}
                  onClose={handleDeleteMenuClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  <MenuItem onClick={handleDeleteThisEvent}>
                    <Typography variant="body">This event</Typography>
                  </MenuItem>
                  {isRecurringEvent && (
                    <MenuItem onClick={handleDeleteAllEvents}>
                      <Typography variant="body">
                        This and all events
                      </Typography>
                    </MenuItem>
                  )}
                </Menu>
              </Box>
            )}
          <IconButton
            onClick={() => onClose()}
            sx={(theme) => ({
              width: "36px",
              height: "36px",
              padding: 0,
              border: `1px solid ${theme.palette.border}`,
              borderRadius: "50%",
              color: theme.palette.text.tertiary,
            })}
          >
            <X size={20} />
          </IconButton>
        </Box>
      </Box>
      <Divider sx={{ marginTop: "22px", marginBottom: "22px" }}></Divider>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : undefined,
          gap: isMobile ? undefined : "64px",
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{}}>
            <Typography sx={{ marginBottom: "12px" }} variant="h4">
              Details
            </Typography>
            {isExternalEvent ? TextDetails(notice!) : Details(event!)}
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "32px",
            minWidth: "270px",
          }}
        >
          {Attendees(isExternalEvent ? eventNotice || notice! : event!)}
          {CalendarInfo(notice!, isTwoWayEditable, onShowDuplicateEvents)}
          {event?.videoRoom && <VideoCallInfo videoRoom={event.videoRoom} />}
          {isExternalEvent
            ? notice && Reminders(notice)
            : event && Reminders(event)}
        </Box>
      </Box>
      {isMobile && (
        <Box sx={{ marginTop: "24px" }}>
          <Button
            sx={{ paddingLeft: "0px" }}
            startIcon={
              <ArrowLeftIcon height="20px" width="20px" color="##198282" />
            }
            variant="text"
            onClick={() => onClose()}
          >
            Back
          </Button>
        </Box>
      )}
    </Box>
  );
};
